import { differenceInDays } from "date-fns/differenceInDays";
import { parseISO } from "date-fns/parseISO";
import { array, bool, number, object, string } from "yup";
import { DRIVE, FLY } from "../shared/constants.js";
import { CHILD_AGE_REQUIRED_BEST_PRICE, DISALLOW_INFANT_INTL_FLIGHT, INFANT_CAPACITY_LIMIT, INVALID_DATES, PACKAGE_LOCATION_REQUIRED } from "../shared/validation/messages.js";
import { isLocationWithInfant } from "../shared/validation/shared-rules.js";
import { asYupResolverSchema } from "../shared/validation/yup-resolver-schema.js";
const MAXIMUM_BUNDLE_DATE_LENGTH = 330;
const LOCATION_REQUIRED = 'Please enter your City, Airport, Point of Interest, Hotel Name or U.S. Zip Code';
const PKG_LOCATION_REQUIRED = PACKAGE_LOCATION_REQUIRED;
const PKG_LOCATION_INCORRECT = 'Please select a city or airport to add a flight to your hotel';
const PKG_LOCATION_INCORRECT_CAR = 'Please select a city or airport to add a car to your hotel';
const INVALID_DATE = INVALID_DATES;
const validation_INFANT_CAPACITY_LIMIT = INFANT_CAPACITY_LIMIT;
const CHILD_AGE_REQUIRED = CHILD_AGE_REQUIRED_BEST_PRICE;
const MAXIMUM_BUNDLE_GUEST_COUNT = 8;
const BUNDLE_MAX_CAPACITY_ERROR = 'Bundles accommodate up to 8 people. You may need an additional bundle for your group';
const MAXIMUM_MULTI_HOTEL_GUEST_COUNT = 8;
const MULTI_HOTEL_MAX_CAPACITY_ERROR = 'Multi-hotel searches support up to 8 people';
const BUNDLE_MAX_DATE_ERROR = `Sorry! We only support bundles up to ${MAXIMUM_BUNDLE_DATE_LENGTH} days in advance. Please choose a date within this window.`;
const numOfValidAges = (childrenAges)=>{
    if ('object' == typeof childrenAges && null !== childrenAges) return Object.values(childrenAges).map((age)=>parseInt(age, 10)).filter((num)=>!Number.isNaN(num) && num >= 0);
    return [];
};
const isInfantLimitReachedPackages = (adultCount, childrenAges, tripType)=>{
    const lapInfants = Object.values(childrenAges).filter((age)=>{
        const numAge = Number.parseInt(age, 10);
        return !Number.isNaN(numAge) && 0 === numAge;
    });
    const infantCount = lapInfants.length;
    return tripType.includes(FLY) && infantCount > adultCount;
};
function isChildrenAgeNotProvided(childrenCount, childrenAges) {
    const validAgesCount = numOfValidAges(childrenAges).length;
    return childrenCount > 0 && childrenCount !== validAgesCount;
}
function handleInfantChildrenTravel(formState) {
    const { childrenAges, adults, children } = this.parent;
    const { startLocation, endLocation, tripType } = formState;
    if (tripType.includes(FLY) && endLocation && startLocation && (isLocationWithInfant(endLocation, childrenAges) || isLocationWithInfant(startLocation, childrenAges))) return this.createError({
        message: DISALLOW_INFANT_INTL_FLIGHT,
        path: this.path
    });
    if (isInfantLimitReachedPackages(adults, childrenAges, tripType)) return this.createError({
        message: validation_INFANT_CAPACITY_LIMIT,
        path: this.path
    });
    if (isChildrenAgeNotProvided(children, childrenAges)) return this.createError({
        message: CHILD_AGE_REQUIRED,
        path: this.path
    });
    return true;
}
function handleRoomCapacity(formState) {
    const { adults, children } = this.parent;
    const { tripType } = formState;
    const travelerCount = adults + children;
    if (MAXIMUM_BUNDLE_GUEST_COUNT < travelerCount && (tripType.includes(FLY) || tripType.includes(DRIVE))) return this.createError({
        message: BUNDLE_MAX_CAPACITY_ERROR,
        path: this.path
    });
    return true;
}
function handleMultiHotelChildrenAges() {
    const { children, childrenAges } = this.parent;
    if (isChildrenAgeNotProvided(children, childrenAges)) return this.createError({
        message: CHILD_AGE_REQUIRED,
        path: this.path
    });
    return true;
}
function handleMultiHotelRoomCapacity() {
    const { adults, children } = this.parent;
    const travelerCount = adults + children;
    if (MAXIMUM_MULTI_HOTEL_GUEST_COUNT < travelerCount) return this.createError({
        message: MULTI_HOTEL_MAX_CAPACITY_ERROR,
        path: this.path
    });
    return true;
}
function handlePackageLocation() {
    const { endLocation, tripType } = this.parent;
    if (!endLocation) return this.createError({
        message: PKG_LOCATION_REQUIRED,
        path: this.path
    });
    if ('CITY' !== endLocation.type && 'AIRPORT' !== endLocation.type && 'GEO' !== endLocation.type && 'POI' !== endLocation.type) {
        const message = tripType.includes(DRIVE) && !tripType.includes(FLY) ? PKG_LOCATION_INCORRECT_CAR : PKG_LOCATION_INCORRECT;
        return this.createError({
            message,
            path: this.path
        });
    }
    return true;
}
function handleDateCheck() {
    const { tripType } = this.parent;
    const dateValue = this.parent[this.path];
    const dateDiff = differenceInDays(parseISO(dateValue), Date.now());
    if (dateDiff > MAXIMUM_BUNDLE_DATE_LENGTH && (tripType.includes(FLY) || tripType.includes(DRIVE))) return this.createError({
        message: BUNDLE_MAX_DATE_ERROR,
        path: this.path
    });
    return true;
}
const hotelSearchFormSchema = asYupResolverSchema(object().shape({
    tripType: string().required(),
    partialStay: bool(),
    startLocation: object().nullable().when('tripType', {
        is: (tripType)=>tripType.includes(FLY),
        then: (schema)=>schema.nullable().required(PKG_LOCATION_REQUIRED)
    }),
    endLocation: object().nullable().when('tripType', {
        is: (tripType)=>tripType.includes(FLY) || tripType.includes(DRIVE),
        then: (schema)=>schema.nullable().test({
                name: 'endLocationPkg',
                test: handlePackageLocation
            }),
        otherwise: (schema)=>schema.nullable().required(LOCATION_REQUIRED)
    }),
    startDate: string().required(INVALID_DATE).test(handleDateCheck),
    endDate: string().required(INVALID_DATE).test(handleDateCheck),
    travelers: object().shape({
        children: number().test({
            name: 'children',
            test () {
                return handleInfantChildrenTravel.call(this, this.from?.[1]?.value);
            }
        }),
        rooms: number().test({
            name: 'rooms',
            test () {
                return handleRoomCapacity.call(this, this.from?.[1]?.value);
            }
        })
    })
}));
const multiHotelSearchFormSchema = asYupResolverSchema(object().shape({
    hotels: array().of(object().shape({
        endLocation: object().nullable().required(LOCATION_REQUIRED),
        startDate: string().nullable().required(INVALID_DATE),
        endDate: string().nullable().required(INVALID_DATE)
    })),
    travelers: object().shape({
        children: number().test({
            name: 'children',
            test: handleMultiHotelChildrenAges
        }),
        rooms: number().test({
            name: 'rooms',
            test: handleMultiHotelRoomCapacity
        })
    })
}));
const validation = hotelSearchFormSchema;
export { BUNDLE_MAX_CAPACITY_ERROR, CHILD_AGE_REQUIRED, validation_INFANT_CAPACITY_LIMIT as INFANT_CAPACITY_LIMIT, INVALID_DATE, LOCATION_REQUIRED, MAXIMUM_MULTI_HOTEL_GUEST_COUNT, MULTI_HOTEL_MAX_CAPACITY_ERROR, PKG_LOCATION_REQUIRED, validation as default, handleMultiHotelChildrenAges, handleMultiHotelRoomCapacity, hotelSearchFormSchema, isInfantLimitReachedPackages, multiHotelSearchFormSchema };
